import { ActionCreatorWithoutPayload, ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { useQueryClient } from '@tanstack/react-query';

import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { ApiContext } from '_lib/api/apiContext';
import {
  AiCallForCallPopDTO,
  Company,
  isPointmanCompany,
  isPoolBrainCompany,
  isServiceTitanApiCompany,
  isServiceTitanPortalV2Company,
  isStCapacityCompany,
  PointmanCompany,
  PoolBrainCompany,
  ServiceTitanApiCompany,
  ServiceTitanMappedCall,
  ServiceTitanPortalV2Company,
  StCapacityCompany
} from '_lib/api/apiTypes.shim';
import { CACHE_PREFIX } from '_lib/api/QueryProvider';
import {
  CompanyContext,
  CompanyProviderData,
  isPointmanCompanyContext,
  isPoolBrainCompanyContext,
  isStApiCompanyContext,
  isStCapacityCompanyContext,
  isStPortalV2CompanyContext,
  reset as resetCompany,
  selectCompanyContext,
  selectSisterCompanyContext,
  setCompanyContext,
  setSisterCompanyContext,
  StCompanyProviderData,
  switchCompany
} from '_lib/callPop/common/CompanyPane/Company.slice';
import { PointmanPhoneCall } from '_lib/callPop/features/PointmanPhoneCall/PointmanPhoneCall';
import {
  reset as resetPointmanPhoneCall,
  toSharedPhoneCallContext as pointmanToShared,
  switchDifferentProviderCompany as switchPointman,
  initialize as initializePointmanSlice
} from '_lib/callPop/features/PointmanPhoneCall/PointmanPhoneCall.slice';

import { PoolBrainPhoneCall } from '_lib/callPop/features/PoolBrainPhoneCall/PoolBrainPhoneCall';
import {
  reset as resetPoolBrainPhoneCall,
  toSharedPhoneCallContext as poolBrainToShared,
  switchDifferentProviderCompany as switchPoolBrain,
  initialize as initializePoolBrainSlice
} from '_lib/callPop/features/PoolBrainPhoneCall/PoolBrainPhoneCall.slice';
import { StApiPhoneCall } from '_lib/callPop/features/StApiPhoneCall/StApiPhoneCall';
import {
  reset as resetStApiPhoneCall,
  toSharedPhoneCallContext as stApiToShared,
  switchDifferentProviderCompany as switchStApi,
  initialize as initializeStApiSlice
} from '_lib/callPop/features/StApiPhoneCall/StApiPhoneCall.slice';
import { StCapacityPhoneCall } from '_lib/callPop/features/StCapacityPhoneCall/StCapacityPhoneCall';
import {
  reset as resetStCapacityPhoneCall,
  toSharedPhoneCallContext as stCapacityToShared,
  switchDifferentProviderCompany as switchStCapacity,
  initialize as initializeStCapacitySlice
} from '_lib/callPop/features/StCapacityPhoneCall/StCapacityPhoneCall.slice';
import { StPortalPhoneCall } from '_lib/callPop/features/StPortalPhoneCall/StPortalPhoneCall';
import {
  reset as resetStPortalPhoneCall,
  toSharedPhoneCallContext as stPortalToShared,
  switchDifferentProviderCompany as switchPortal,
  initialize as initializeStPortalSlice
} from '_lib/callPop/features/StPortalPhoneCall/StPortalPhoneCall.slice';
import { InvalidCallerIdDialog } from '_lib/callPop/InvalidCallerIdDialog';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';

import { ManualErrorBoundary, RecoverableErrorState } from '_lib/components/ManualErrorBoundary/ManualErrorBoundary';

import { useAlertDialog } from '_lib/hooks/AlertDialogHook';
import { useAppDispatch, useAppSelector, useAppStore } from '_lib/redux/hooks';
import { RootState } from '_lib/redux/store';
import {
  CallerIdState,
  PhoneCallURLState,
  SharedPhoneCallContext,
  SwitchProviderCompanyPayload
} from '_lib/types/PhoneCall.types';
import { formatPhone, hasTenDigits } from '_lib/utils/helpers';
import { logDebug } from '_lib/utils/logging';
import { reportException } from '_lib/utils/sentryService';

import { initPhoneCallUrlState, toCallerIdState } from '_lib/utils/urlStateUtils';

import { isMagicAiCallIdDemoNumber, mockAiCallForCallPopDTO } from './aiCallDemo';
import { useCallLogNavigate } from './useCallLogNavigate';

/**
 * NOTE - may be more conditions added in the future as more providers are supported
 */
function isSchedulingRequired(company: Company) {
  return isServiceTitanApiCompany(company);
}

type CommonCompanyProviderState = {
  commonPhoneCallData: SharedPhoneCallContext;
  resetPhoneCallAction: ActionCreatorWithoutPayload;
  switchDifferentProviderCompanyAction: ActionCreatorWithPayload<SwitchProviderCompanyPayload>;
};

type CompanyProviderState =
  | ({
      company: ServiceTitanApiCompany;
      initializePhoneCallSlice: () => ReturnType<typeof initializeStApiSlice>;
    } & CommonCompanyProviderState)
  | ({
      company: StCapacityCompany;
      initializePhoneCallSlice: () => ReturnType<typeof initializeStCapacitySlice>;
    } & CommonCompanyProviderState)
  | ({
      company: ServiceTitanPortalV2Company;
      initializePhoneCallSlice: () => ReturnType<typeof initializeStPortalSlice>;
    } & CommonCompanyProviderState)
  | ({
      company: PoolBrainCompany;
      initializePhoneCallSlice: () => ReturnType<typeof initializePoolBrainSlice>;
    } & CommonCompanyProviderState)
  | ({
      company: PointmanCompany;
      initializePhoneCallSlice: () => ReturnType<typeof initializePointmanSlice>;
    } & CommonCompanyProviderState);

const getCompanyProviderState = (rootState: RootState, company?: Company): CompanyProviderState | null => {
  if (!company) return null;
  else if (isServiceTitanApiCompany(company)) {
    return {
      company,
      commonPhoneCallData: stApiToShared(rootState.stApiPhoneCallSlice.phoneCallContext),
      resetPhoneCallAction: resetStApiPhoneCall,
      switchDifferentProviderCompanyAction: switchStApi,
      initializePhoneCallSlice: () => initializeStApiSlice({ company })
    };
  } else if (isStCapacityCompany(company)) {
    return {
      company,
      commonPhoneCallData: stCapacityToShared(rootState.stCapacityPhoneCallSlice.phoneCallContext),
      resetPhoneCallAction: resetStCapacityPhoneCall,
      switchDifferentProviderCompanyAction: switchStCapacity,
      initializePhoneCallSlice: () => initializeStCapacitySlice({ company })
    };
  } else if (isServiceTitanPortalV2Company(company)) {
    return {
      company,
      commonPhoneCallData: stPortalToShared(rootState.stPortalPhoneCallSlice.phoneCallContext),
      resetPhoneCallAction: resetStPortalPhoneCall,
      switchDifferentProviderCompanyAction: switchPortal,
      initializePhoneCallSlice: () => initializeStPortalSlice({ company })
    };
  } else if (isPoolBrainCompany(company)) {
    return {
      company,
      commonPhoneCallData: poolBrainToShared(rootState.poolBrainPhoneCallSlice.phoneCallContext),
      resetPhoneCallAction: resetPoolBrainPhoneCall,
      switchDifferentProviderCompanyAction: switchPoolBrain,
      initializePhoneCallSlice: () => initializePoolBrainSlice({ company })
    };
  } else if (isPointmanCompany(company)) {
    return {
      company,
      commonPhoneCallData: pointmanToShared(rootState.pointmanPhoneCallSlice.phoneCallContext),
      resetPhoneCallAction: resetPointmanPhoneCall,
      switchDifferentProviderCompanyAction: switchPointman,
      initializePhoneCallSlice: () => initializePointmanSlice({ company })
    };
  } else {
    return null;
  }
};

export const CallPop = (): React.ReactElement => {
  const params = useParams();
  const location = useLocation();

  const { navigateToCallLog } = useCallLogNavigate();

  const api = useContext(ApiContext);

  const [callerIdState, setCallerIdState] = useState<CallerIdState>(() => toCallerIdState(params.callerId));
  const [phoneCallURLState, setPhoneCallURLState] = useState<PhoneCallURLState>(
    initPhoneCallUrlState({
      callerId: callerIdState.callerId,
      companyDNIS: params.companyDNIS,
      locationSearch: location.search
    })
  );
  const [mappedCall, setMappedCall] = useState<ServiceTitanMappedCall | null>(null);
  const [aiCall, setAiCall] = useState<AiCallForCallPopDTO['aiCall'] | null>(null);
  const [isSwitchingCompany, setIsSwitchingCompany] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  const [recoverableError, setRecoverableError] = useState<RecoverableErrorState | null>(null);
  const alertDialog = useAlertDialog();

  const dispatch = useAppDispatch();
  const companyContext = useAppSelector(selectCompanyContext);
  const sisterCompanyContext = useAppSelector(selectSisterCompanyContext);

  const store = useAppStore();

  const queryClient = useQueryClient();

  const setSisterCompaniesInBackground = async (company: CompanyContext['company']) => {
    logDebug('// - setSisterCompaniesInBackground - //');
    try {
      const sisterCompanies = company.sisterCompanies.length
        ? await Promise.all(
            company.sisterCompanies.map((sc) => api.companies.getById({ id: sc.id, disableErrorReporting: true }))
          )
        : [];

      dispatch(setSisterCompanyContext({ sisterCompanies }));
    } catch (err) {
      reportException(err);
      dispatch(setSisterCompanyContext(undefined));
    }
  };

  const fetchAndSetMappedCall = async (companyId: string, callerId: string) => {
    logDebug('// - fetchMappedCall - //');
    try {
      const mappedCall = await api.serviceTitan(companyId).mappedCalls.getMappedCallOrNull(callerId);
      if (mappedCall) setMappedCall(mappedCall);
    } catch (error) {
      reportException(error);
      setMappedCall(null);
    }
  };

  const initializeCallPop = async () => {
    logDebug('// - initializeCallPop - //');

    let company: Company | null = null;
    let aiCallDetail: AiCallForCallPopDTO['aiCall'] | null = null;
    try {
      setIsInitializing(true);

      if (
        phoneCallURLState.aiCallId &&
        phoneCallURLState.companyDNIS &&
        isMagicAiCallIdDemoNumber(phoneCallURLState.aiCallId)
      ) {
        /**
         * ⚠️☢️ DEMO MODE START ☢️⚠️
         *
         * If we are in demo mode, first fetch the company by companyDNIS, then fill in the aiCall with mocked data
         */
        company = await api.companies.getCompanyByDnis(phoneCallURLState.companyDNIS);
        if (!company) {
          const err = new Error('Company not found for DNIS');
          setRecoverableError({
            error: err,
            message: `Failed to load company - Company not found for DNIS: ${formatPhone(phoneCallURLState.companyDNIS)}`,
            shouldReport: true
          });
          setIsInitializing(false);
          return;
        }
        aiCallDetail = mockAiCallForCallPopDTO({
          company,
          companyDNIS: phoneCallURLState.companyDNIS,
          aiCallId: phoneCallURLState.aiCallId,
          callerId: callerIdState.callerId
        });
        setAiCall(aiCallDetail);
        /**
         * ⚠️☢️ DEMO MODE END ☢️⚠️
         */
      } else if (phoneCallURLState.aiCallId) {
        const { aiCall: aiCallResponse, company: companyDetail } = await api.aiCalls.getAiCallForCallPop({
          id: phoneCallURLState.aiCallId
        });
        aiCallDetail = aiCallResponse;
        company = companyDetail;

        setAiCall(aiCallDetail);

        // Also need to set DNIS from the aiCall data
        const companyDNIS = aiCallDetail?.companyDNIS;
        if (companyDNIS) setPhoneCallURLState((prev) => ({ ...prev, companyDNIS: companyDNIS }));
      } else if (phoneCallURLState.companyId) {
        company = await api.companies.getById({ id: phoneCallURLState.companyId });
      } else if (phoneCallURLState.companyDNIS) {
        company = await api.companies.getCompanyByDnis(phoneCallURLState.companyDNIS);
      } else {
        const err = new Error('phoneCallURLState missing companyDNIS and companyId');
        setRecoverableError({ error: err, message: 'Failed to load company: missing DNIS', shouldReport: true });
        setIsInitializing(false);
        return;
      }

      if (!company) {
        setIsInitializing(false);
        await alertDialog.show({
          variant: 'error',
          titleText: 'UNKNOWN COMPANY',
          content: phoneCallURLState.companyDNIS
            ? `There was no company found for DNIS: ${formatPhone(phoneCallURLState.companyDNIS)}`
            : 'Unable to find company without a DNIS number.',
          acknowledgeButtonText: 'LEAVE CALL'
        });
        navigateToCallLog();
        return;
      }

      const rootState = store.getState();

      const companyProviderState = getCompanyProviderState(rootState, company);
      if (!companyProviderState) {
        setIsInitializing(false);
        reportException(new Error('ST Call Pop - triggered for no st api or portal company'), { extra: { company } });

        await alertDialog.show({
          variant: 'error',
          titleText: 'Non-Supported Company',
          content: `Company is not supported. Notify supervisor that ${company.name} is not configured.`,
          acknowledgeButtonText: 'LEAVE CALL'
        });

        navigateToCallLog();
        return;
      }

      if (isSchedulingRequired(company) && !company.isScheduleEnabled) {
        setIsInitializing(false);
        await alertDialog.show({
          variant: 'error',
          titleText: 'SCHEDULING NOT ENABLED FOR COMPANY',
          content: `Please notify your supervisor that ${company.name} does not have scheduling enabled.`,
          acknowledgeButtonText: 'LEAVE CALL'
        });
        navigateToCallLog();
        return;
      }

      let companyProviderData: CompanyProviderData;

      if (
        isServiceTitanApiCompany(companyProviderState.company) ||
        isStCapacityCompany(companyProviderState.company) ||
        isServiceTitanPortalV2Company(companyProviderState.company)
      ) {
        companyProviderData = {
          originalCompanyTenantId: companyProviderState.company.dataProvider.providerOptions.tenantId
        } satisfies StCompanyProviderData;
      }

      const companyCtx: CompanyContext = {
        company,
        originalCompanyName: aiCallDetail && aiCallDetail.originalCompany ? aiCallDetail.originalCompany : company.name,
        switched: aiCallDetail ? aiCallDetail.companySwitched : false,
        companyProviderData
      };
      dispatch(setCompanyContext(companyCtx));

      dispatch(companyProviderState.initializePhoneCallSlice());

      const appConfig = await api.appConfig.getAppConfig();
      const isShellCompany = appConfig.companyConfigItems?.some(
        (o) => o.isCallPopShellCompany && o.companyId === companyCtx.company.id
      );
      if (
        phoneCallURLState.callerId &&
        hasTenDigits(phoneCallURLState.callerId) &&
        !isShellCompany &&
        (isServiceTitanApiCompany(company) || isServiceTitanPortalV2Company(company) || isStCapacityCompany(company))
      ) {
        fetchAndSetMappedCall(company.id, phoneCallURLState.callerId);
      }
      setSisterCompaniesInBackground(company);
    } catch (err) {
      setRecoverableError({ error: err, message: 'Failed to load company: Network error', shouldReport: true });
    } finally {
      setIsInitializing(false);
    }
  };

  /**
   * Switches the company in context. Used to switch between sister companies on a call.
   */
  const handleSwitchCompany = async (companyId: string) => {
    let returnValue: Company | null = null;

    const rootState = store.getState();

    const currentCompany = companyContext?.company;
    const currentCompanyProviderState = getCompanyProviderState(rootState, currentCompany);

    // Paranoid/typescript check. It (should be) impossible to trigger a company switch without companyContext bc...
    // a company (therefore companyContext) is required to even display the switching buttons :)
    if (!companyContext || !currentCompany || !currentCompanyProviderState) {
      setRecoverableError({ error: new Error('switchCompany called without companyContext'), shouldReport: true });
      return returnValue;
    }
    logDebug('// - handleSwitchCompany - //');
    try {
      setIsSwitchingCompany(true);

      // Get the sister company from the cached list OR fetch it fresh from API
      let newCompany = sisterCompanyContext?.sisterCompanies.find((sc) => sc.id === companyId);
      if (!newCompany) newCompany = await api.companies.getById({ id: companyId });

      const newCompanyProviderState = getCompanyProviderState(rootState, newCompany);
      if (!newCompany || !newCompanyProviderState) {
        setIsSwitchingCompany(false);
        await alertDialog.show({
          variant: 'warning',
          titleText: 'CANNOT SWITCH COMPANY',
          content: [
            `Cannot switch the current company to ${newCompany.name}.`,
            'Sister company uses different customer management software.'
          ]
        });
        return null;
      }

      if (isSchedulingRequired(newCompany) && !newCompany.isScheduleEnabled) {
        setIsSwitchingCompany(false);
        await alertDialog.show({
          variant: 'warning',
          titleText: 'CANNOT SWITCH COMPANY',
          content: [
            `Cannot switch the current company to ${newCompany.name}.`,
            `Please notify your supervisor that "${newCompany.name}" does not have scheduling enabled.`
          ]
        });
        return null;
      }

      dispatch(switchCompany(newCompany));
      setSisterCompaniesInBackground(newCompany);

      if (
        currentCompany.dataProvider?.providerType !== newCompany.dataProvider?.providerType ||
        currentCompany.dataProvider?.providerVersion !== newCompany.dataProvider?.providerVersion
      ) {
        // If switching providers, keep subset of reusable phone call data, regardless of provider
        dispatch(currentCompanyProviderState.resetPhoneCallAction());
        dispatch(
          newCompanyProviderState.switchDifferentProviderCompanyAction({
            ...currentCompanyProviderState.commonPhoneCallData,
            company: newCompany
          })
        );
      }

      returnValue = newCompany;
    } catch (err) {
      reportException(err);
    } finally {
      setIsSwitchingCompany(false);
    }
    return returnValue;
  };

  const firstRenderRouteState = useRef(true);
  useEffect(() => {
    // Only need this to run if url params/search have changed
    // initial phoneCallUrlState is already set with valid params/search, so we can bypass first render
    if (firstRenderRouteState.current) {
      firstRenderRouteState.current = false;
      return;
    } else {
      setPhoneCallURLState((prev) => ({ ...prev, callerId: callerIdState.callerId }));
    }
  }, [callerIdState.callerId]);

  /**
   * On App mount ensure redux cache is reset for all slices and fetch company
   * Also clear out any non-CACHE queries
   */
  useEffect(() => {
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== CACHE_PREFIX });

    dispatch(resetCompany());
    dispatch(resetPoolBrainPhoneCall());
    dispatch(resetStPortalPhoneCall());
    dispatch(resetStApiPhoneCall());
    dispatch(resetStCapacityPhoneCall());
    dispatch(resetPoolBrainPhoneCall());
    dispatch(resetPointmanPhoneCall());
    initializeCallPop();
    return () => {
      dispatch(resetCompany());
      dispatch(resetPoolBrainPhoneCall());
      dispatch(resetStPortalPhoneCall());
      dispatch(resetStApiPhoneCall());
      dispatch(resetStCapacityPhoneCall());
      dispatch(resetPoolBrainPhoneCall());
      dispatch(resetPointmanPhoneCall());
    };
    // only run on first mount (& unmount)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FlexBox sx={{ height: '100vh', width: '100%', flexDirection: 'column', overflow: 'hidden' }}>
      <BackdropSpinner open={isSwitchingCompany || isInitializing} />
      {!recoverableError && isStApiCompanyContext(companyContext) && (
        <StApiPhoneCall
          aiCall={aiCall}
          companyContext={companyContext}
          phoneCallURLState={phoneCallURLState}
          mappedCall={mappedCall}
          handleSwitchCompany={handleSwitchCompany}
        />
      )}
      {!recoverableError && isStCapacityCompanyContext(companyContext) && (
        <StCapacityPhoneCall
          companyContext={companyContext}
          phoneCallURLState={phoneCallURLState}
          mappedCall={mappedCall}
          handleSwitchCompany={handleSwitchCompany}
        />
      )}
      {!recoverableError && isStPortalV2CompanyContext(companyContext) && (
        <StPortalPhoneCall
          companyContext={companyContext}
          phoneCallURLState={phoneCallURLState}
          callerIdState={callerIdState}
          mappedCall={mappedCall}
          aiCall={aiCall}
          handleSwitchCompany={handleSwitchCompany}
        />
      )}
      {!recoverableError && isPoolBrainCompanyContext(companyContext) && (
        <PoolBrainPhoneCall
          companyContext={companyContext}
          phoneCallURLState={phoneCallURLState}
          aiCall={aiCall}
          handleSwitchCompany={handleSwitchCompany}
        />
      )}
      {!recoverableError && isPointmanCompanyContext(companyContext) && (
        <PointmanPhoneCall
          companyContext={companyContext}
          phoneCallURLState={phoneCallURLState}
          aiCall={aiCall}
          handleSwitchCompany={handleSwitchCompany}
        />
      )}
      {recoverableError && (
        <ManualErrorBoundary
          errorState={recoverableError}
          reportingCtx={{ extra: { isInitializing, isSwitchingCompany, companyContext, phoneCallURLState } }}
          onRefresh={async () => {
            setRecoverableError(null);
            await initializeCallPop();
          }}
        />
      )}
      {/* NOTE - this is a dialog that pops on load based on URL state, so this approach is appropriate */}
      {!callerIdState.isValid && (
        <InvalidCallerIdDialog
          open={!callerIdState.isValid}
          invalidCallerId={params.callerId}
          onClose={(validCallerId) => setCallerIdState(toCallerIdState(validCallerId))}
        />
      )}
      {alertDialog.element}
    </FlexBox>
  );
};
